import {AfterViewInit, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NavigationEnd, Router, RouterLink, RouterOutlet} from '@angular/router';
import { DacklTabsItem, DacklTabsComponent, DacklEmptyStateComponent } from '@datev/ngx-dackl';
import {AuthenticationService} from '@datev/ngx-dcal';
import {combineLatest, Observable, Subject} from 'rxjs';
import {filter, map, startWith, takeUntil, tap} from 'rxjs/operators';
import {Download, isMainRelease, isPackage, isProduct, isServiceRelease} from '../../model';
import {DownloadStore} from '../../shared/services/download-store';
import {RouteLocatorService} from '../../shared/services/route-locator.service';
import '@datev/dacks/content';
import '@datev/dacks/content-right-aside';
import { DownloadInfoCardComponent } from '../download-info-card/download-info-card.component';
import { MatAnchor } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTabNavPanel } from '@angular/material/tabs';
import { PushPipe } from '@ngrx/component';
import { FooterSpacing } from 'projects/myupdates/src/app/footer/footer-spacing';

@Component({
    selector: 'dp-download-list-layout',
    templateUrl: './download-list-layout.component.html',
    styleUrl: './download-list-layout.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
      DacklTabsComponent,
      DownloadInfoCardComponent,
      RouterLink,
      MatAnchor,
      MatIcon,
      MatTabNavPanel,
      DacklEmptyStateComponent,
      PushPipe,
      RouterOutlet,
      FooterSpacing
  ],
})
export class DownloadListLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly TABLIST_ARIA_LABEL = 'Downloadbereich auswählen';

  private readonly _navigationItems: Array<DacklTabsItem> = [
    { text: 'Basis-Software', path: 'lists/packages' },
    { text: 'Zusatz-Software', path: 'lists/products' },
    { text: 'Mittelstand (Freischaltcode)', path: 'lists/sme' },
  ];

  public navigationItems: Array<DacklTabsItem> = this._navigationItems;

  public shouldDisplayDetailLink$: Observable<boolean> =
    this.routeLocatorService.isOnPilotToggleRelevantListPage$.pipe(
      map(isOnRelevantPage => !isOnRelevantPage),
    );

  public selectedDownload$ = this.downloadStore.selectedDownload$.pipe(
    map(download => {
      if (download) {
        return { ...download, detailUrl: this.getDetailUrl(download) };
      }

      return undefined;
    }),
  );

  public hideAside$ = combineLatest([
    this.routeLocatorService.isOnBaseSoftwareListPage$,
    this.selectedDownload$,
    this.auth.isLoggedIn.asObservable().pipe(startWith(false)),
  ]).pipe(
    map(
      ([isOnBaseSoftwareListPage, selectedDownload, isLoggedIn]) => isOnBaseSoftwareListPage && !isLoggedIn || !selectedDownload,
    ),
    tap((hideAside) => {
        this.dacksContent?.nativeElement.dispatchEvent(new Event(hideAside ? 'right-aside-close-click' : 'right-aside-show-click'));
    })
  );

  @ViewChild('dacksContent')
  public dacksContent: ElementRef<HTMLElement> | undefined;

  @ViewChild('detailLink', { read: ElementRef })
  public set detailLink(ref: ElementRef<HTMLElement> | undefined) {
    if (ref && this.downloadStore.consumeDetailLinkFocusRequest()) {
      this.focusDetailLink(ref.nativeElement);
    }
  }

  @ViewChild(DacklTabsComponent, { read: ElementRef })
  public downloadTabsElement: ElementRef<HTMLElement> | undefined;

  private tablistObserver?: MutationObserver;
  private readonly destroyed$ = new Subject<void>();

  /**
   * Focuses the "Detailseite aufrufen" link after returning from the details
   * page. The right-aside opens asynchronously (via a dispatched event and an
   * open animation), so the link is not focusable in the same change-detection
   * cycle in which it is rendered. We therefore retry on each animation frame
   * until the element is actually focused or a safety limit is reached.
   */
  private focusDetailLink(element: HTMLElement, remainingAttempts = 30): void {
    element.focus();

    if (this.isElementFocused(element) || remainingAttempts <= 0) {
      return;
    }

    requestAnimationFrame(() =>
      this.focusDetailLink(element, remainingAttempts - 1),
    );
  }

  private isElementFocused(element: HTMLElement): boolean {
    let active: Element | null = document.activeElement;

    while (active) {
      if (active === element) {
        return true;
      }

      active = active.shadowRoot?.activeElement ?? null;
    }

    return false;
  }

  constructor(
    private readonly router: Router,
    private readonly downloadStore: DownloadStore,
    private readonly routeLocatorService: RouteLocatorService,
    private readonly auth: AuthenticationService,
    private readonly changeDetectorRef?: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const childRoot = this.router.routerState.snapshot.url
      .split('/')
      .filter(urlSegment => urlSegment !== '')[0];

    this.navigationItems = this._navigationItems.map(navItem => ({
      ...navItem,
      path: `/${childRoot}/${navItem.path}`,
    }));
  }

  ngAfterViewInit(): void {
    this.applyTablistAriaLabel();

    if (this.downloadTabsElement) {
      this.tablistObserver = new MutationObserver(() => this.applyTablistAriaLabel());
      this.tablistObserver.observe(this.downloadTabsElement.nativeElement, {
        childList: true,
        subtree: true,
      });
    }

    // MatTabNavPanel setzt aria-labelledby verzögert; eine zusätzliche CD stabilisiert NG0100 in Dev-Mode.
    this.changeDetectorRef?.detectChanges();
    queueMicrotask(() => { if (!this.destroyed$.isStopped) { this.changeDetectorRef?.detectChanges(); } });

    // Beim Tabwechsel (Router-Navigation) aktualisiert MatTabNavPanel aria-labelledby erneut verzögert.
    // Deshalb nach jeder abgeschlossenen Navigation im Download-Bereich nochmal synchronisieren.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter(() => this.router.url.includes('/lists/')),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.changeDetectorRef?.detectChanges();
        queueMicrotask(() => this.changeDetectorRef?.detectChanges());
      });
  }

  ngOnDestroy(): void {
    this.tablistObserver?.disconnect();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private applyTablistAriaLabel(): void {
    const tablist = this.downloadTabsElement?.nativeElement.querySelector('nav[role="tablist"]');

    if (tablist) {
      tablist.setAttribute('aria-label', DownloadListLayoutComponent.TABLIST_ARIA_LABEL);
    }
  }

  private getDetailUrl(download: Download): string {
    if (isPackage(download)) {
      return `../packages/${download.id}`;
    }

    if (isProduct(download)) {
      return `../products/${download.id}`;
    }

    if (isMainRelease(download)) {
      return `../main-releases/${download.id}`;
    }

    if (isServiceRelease(download)) {
      return `../service-releases/${download.id}`;
    }

    return '';
  }
}
